import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useGameInitialization } from './hooks';
import { IUseGamecast, IUseGamecastProps } from './interfaces';
import { getUserBrowser } from './helpers';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { useConference } from '../Dolby';
import { MytaverseLogger } from '@helpers/logger';
import { useLocation } from 'react-router-dom';
import { useEventContext } from '@providers/EventProvider';
import ROUTES from '@constants/routes';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { useConferenceState } from '@hooks/conferenceContext';
import { AnalyticsType } from '@interfaces/analytics';
import { default as gameliftStreamsLogger } from './logger';
import { IParticipant } from '@interfaces/participants';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { getNotification } from '@components/Notification/helpers';
import { useTranslation } from 'react-i18next';
import { getClientSpecificIsGameCastActionSend } from '@helpers/clientSpecificUtils';

const GAMECAST_VIDEO_TAG_ID = 'gamecastVideoTag';
const GAMECAST_AUDIO_TAG_ID = 'gamecastAudioTag';
const MAX_GAMECAST_RECONNECT_ATTEMPTS = 2;
const GAMECAST_RECONNECT_COOLDOWN_MS = 1500;

export const useGamecast = ({
  currentEventId,
  currentParticipant,
  websocketSessionId,
  currentRoom,
  gameSound,
  setGameCastStreamId,
  setGameCastStreamRegion,
  initMessageHandler,
  gameCastStreamId,
  gameCastStreamRegion,
  gameCastStreamRequestSent,
  setGameCastStreamRequestSent,
  setInitMessageSent,
  setStreamingProvider,
  setIsGamecastCrash,
  setCurrentRoom,
  setCurrentParticipant,
  isGamecastCrash,
  gamecastStats,
  currentEvent,
  trackAnalytics,
  isGamecastTimeout,
  setIsGamecastTimeout,
  pixelStreamingClient,
  externalKeyboardConnected,
  setShowExternalKeyboardOverlay,
}: IUseGamecastProps): IUseGamecast => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const connectionTimer = useRef<number | null>(0);
  const browserType = getUserBrowser();
  const [isStreamLoading, setIsStreamLoading] = React.useState(false);
  const [isStreamLoaded, setIsStreamLoaded] = React.useState(false);
  const { conference } = useConference();
  const { showNotification, hideNotification } = useNotification();
  const { t: translate } = useTranslation();
  const {
    sendJSONMessageToWebSocket,
    user,
    isDebugMode,
    navigateToEventsPage,
  } = useMytaverse();
  const { pathname } = useLocation();
  const { isEventRun, setIsEventRun, launchArgs } = useEventContext();
  const { activeSpeakerDeviceId } = useConferenceState();
  const [shouldReconnectGamecast, setShouldReconnectGamecast] =
    React.useState(false);
  const [isReconnectLimitReached, setIsReconnectLimitReached] =
    React.useState(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectInProgressRef = useRef(false);
  const pendingReconnectRequestRef = useRef(false);
  const reconnectCooldownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectNotificationRef = useRef<string | number | null>(null);
  const maxGamecastReconnectAttempts = useMemo(
    () =>
      currentEvent?.settings?.generalEnablePureGamecast
        ? 0
        : MAX_GAMECAST_RECONNECT_ATTEMPTS,
    [currentEvent],
  );

  const resetReconnectGamecastAttempts = React.useCallback(() => {
    reconnectAttemptsRef.current = 0;
  }, []);

  const setReconnectGamecast: React.Dispatch<React.SetStateAction<boolean>> =
    React.useCallback(
      (next) => {
        setShouldReconnectGamecast((prev) => {
          const resolvedNext =
            typeof next === 'function'
              ? (next as (prevState: boolean) => boolean)(prev)
              : next;

          if (resolvedNext === true && prev === false) {
            if (reconnectInProgressRef.current) {
              gameliftStreamsLogger.log(
                'Queue reconnect: reconnect already in progress',
              );
              pendingReconnectRequestRef.current = true;
              return prev;
            }

            if (reconnectAttemptsRef.current >= maxGamecastReconnectAttempts) {
              gameliftStreamsLogger.log(
                `Skip reconnect: max retries reached (${maxGamecastReconnectAttempts})`,
              );
              setIsReconnectLimitReached(true);
              return prev;
            }
          }

          return resolvedNext;
        });
      },
      [maxGamecastReconnectAttempts],
    );

  const hideReconnectNotification = useCallback(() => {
    if (reconnectNotificationRef.current) {
      gameliftStreamsLogger.log('Hide reconnect notification');
      hideNotification(reconnectNotificationRef.current);
      reconnectNotificationRef.current = null;
    }
  }, [hideNotification]);

  useEffect(() => {
    if (!isReconnectLimitReached) {
      return;
    }

    hideReconnectNotification();

    navigateToEventsPage({
      message: 'notifications.noAvailableStreamingProviders',
      isModal: false,
    });
  }, [
    isReconnectLimitReached,
    navigateToEventsPage,
    hideReconnectNotification,
  ]);

  useEffect(() => {
    if (reconnectNotificationRef.current && currentRoom) {
      hideReconnectNotification();
    }
  }, [currentRoom, hideReconnectNotification]);

  useEffect(() => {
    return () => {
      if (reconnectCooldownTimeoutRef.current) {
        clearTimeout(reconnectCooldownTimeoutRef.current);
        reconnectCooldownTimeoutRef.current = null;
      }
    };
  }, []);

  const sendMytaverseActions = React.useCallback(
    (currentEventParticipant: IEventParticipant) => {
      const actionMessage = getClientSpecificIsGameCastActionSend({
        user,
        currentEventParticipant,
      });
      if (actionMessage) {
        sendJSONMessageToWebSocket(actionMessage);
      }
    },
    [sendJSONMessageToWebSocket],
  );

  const {
    initializeGamecastStream,
    closeGamecastStream,
    abortGamecastConnection,
    handleReconnectGamecast,
    gamecastClientRef,
    processActiveStreamSession,
    initializeGamecastConnectionTimeout,
    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
  } = useGameInitialization({
    browserType,
    streamId: gameCastStreamId,
    currentParticipant,
    setStreamingProvider,
    setIsGamecastCrash,
    setIsGamecastTimeout,
    gamecastStats,
    currentRoom,
    externalKeyboardConnected,
    setShowExternalKeyboardOverlay,
  });

  useAsyncEffect(async () => {
    if (
      isStreamLoading ||
      !currentParticipant ||
      gameCastStreamRequestSent ||
      !gameCastStreamId ||
      !gameCastStreamRegion ||
      !initMessageHandler ||
      gamecastClientRef.current ||
      shouldReconnectGamecast
    ) {
      return;
    }

    if (isDebugMode) {
      const initMessage = await initMessageHandler();
      console.log({ initMessage });
      setInitMessageSent(true);
      setIsStreamLoaded(true);
      return;
    }

    try {
      setIsStreamLoading(true);
      connectionTimer.current = new Date().getTime();
      initializeGamecastConnectionTimeout();
      initGameliftStreamsSessionStatusTimeout();

      setGameCastStreamRequestSent(true);
      setInitMessageSent(true);

      videoRef.current = document.getElementById(
        GAMECAST_VIDEO_TAG_ID,
      ) as HTMLVideoElement;

      audioRef.current = document.getElementById(
        GAMECAST_AUDIO_TAG_ID,
      ) as HTMLAudioElement;

      trackAnalytics(AnalyticsType.StartGameLiftStreamsInit);

      const additionalLaunchArgs = [
        ...(launchArgs || []),
        ...(pixelStreamingClient?.clientParams?.additionalLaunchArgs || []),
      ];

      await initializeGamecastStream(
        currentParticipant.participantId,
        websocketSessionId,
        initMessageHandler,
        gameCastStreamRegion,
        additionalLaunchArgs,
      );
    } catch (error) {
      setIsStreamLoading(false);
    }
  }, [
    currentParticipant,
    currentEvent,
    initMessageHandler,
    gameCastStreamRequestSent,
    gameCastStreamRegion,
    initializeGamecastStream,
    isDebugMode,
    isStreamLoading,
    gameCastStreamId,
    initializeGamecastConnectionTimeout,
    pixelStreamingClient,
  ]);

  useEffect(() => {
    return () => {
      setGameCastStreamId('');
      setGameCastStreamRegion('');
    };
  }, []);

  useEffect(() => {
    if (
      (!currentEvent?.settings?.generalEnablePureGamecast && !currentRoom) ||
      !isStreamLoaded ||
      !currentEventId ||
      !isEventRun ||
      pathname !== ROUTES.EVENT_PAGE(currentEventId)
    ) {
      return;
    }

    const videoElement = document.getElementById(GAMECAST_VIDEO_TAG_ID);

    if (!videoElement) {
      return;
    }

    videoElement.style.position = 'absolute';
    videoElement.style.left = '0';
    videoElement.style.top = '0';
    videoElement.style.zIndex = '-1';
    videoElement.style.visibility = 'visible';
    videoElement.style.width = '100%';
    videoElement.style.height = '100vh';
    videoElement.style.maxHeight = '100%';

    try {
      gamecastClientRef.current?.attachInput?.();
    } catch (e) {
      console.error(e);
    }
  }, [
    currentRoom,
    isStreamLoaded,
    videoRef,
    currentEventId,
    pathname,
    isEventRun,
  ]);

  useAsyncEffect(async () => {
    const isSinglePlayer = currentEvent?.eventTags?.includes('SinglePlayer');

    if (audioRef.current && (isSinglePlayer || currentRoom) && isEventRun) {
      try {
        //@ts-ignore
        await audioRef.current.setSinkId(activeSpeakerDeviceId);
      } catch (e) {
        console.error('Error setting audio output:', e);
      }

      audioRef.current.muted = false;

      audioRef.current.volume = isSinglePlayer ? 1 : gameSound;
      await audioRef.current.play();
    }
  }, [
    gameSound,
    audioRef,
    conference,
    currentRoom,
    isEventRun,
    activeSpeakerDeviceId,
    currentEvent,
  ]);

  useAsyncEffect(async () => {
    if (!currentRoom) {
      return;
    }

    if (connectionTimer.current) {
      const connectionTime = new Date().getTime() - connectionTimer.current;

      gameliftStreamsLogger.log(
        `GAMELIFT STREAMS CONNECTION TIME ${connectionTime / 1000}s`,
      );
      connectionTimer.current = null;
    }
  }, [currentRoom]);

  useAsyncEffect(() => {
    const browserType = getUserBrowser();

    MytaverseLogger.log(`ENTERING BY ${browserType} BROWSER`);
  }, []);

  useEffect(() => {
    if ((isGamecastCrash || isGamecastTimeout) && !shouldReconnectGamecast) {
      setReconnectGamecast(true);

      if (isGamecastCrash) {
        trackAnalytics(AnalyticsType.EventSessionCrash);
        setIsGamecastCrash(false);
      }

      if (isGamecastTimeout) {
        trackAnalytics(AnalyticsType.EventSessionTimeout);
        setIsGamecastCrash(false);
      }
    }
  }, [isGamecastCrash, isGamecastTimeout, shouldReconnectGamecast]);

  useAsyncEffect(async () => {
    if (
      !shouldReconnectGamecast ||
      !gameCastStreamRegion ||
      !currentParticipant ||
      !initMessageHandler
    ) {
      return;
    }

    setShouldReconnectGamecast(false);

    if (reconnectAttemptsRef.current >= maxGamecastReconnectAttempts) {
      setIsReconnectLimitReached(true);
      return;
    }

    if (!reconnectNotificationRef.current && currentRoom) {
      reconnectNotificationRef.current = showNotification(
        getNotification({
          message: translate('notifications.kickOutError'),
          type: NOTIFICATION_TYPES.ERROR,
          closeManually: true,
          withDismissButton: false,
        }),
      );
    }

    reconnectAttemptsRef.current += 1;
    gameliftStreamsLogger.log(
      `Reconnect attempt ${reconnectAttemptsRef.current}/${maxGamecastReconnectAttempts}`,
    );

    reconnectInProgressRef.current = true;
    pendingReconnectRequestRef.current = false;
    setCurrentRoom(null);
    setCurrentParticipant((prev) => ({
      ...(prev ? (prev as IParticipant) : ({} as IParticipant)),
      roomId: null,
    }));

    setIsEventRun(false);
    setIsStreamLoading(true);
    setIsStreamLoaded(false);

    setIsGamecastCrash(false);
    setIsGamecastTimeout(false);

    const additionalLaunchArgs = [
      ...(launchArgs || []),
      ...(pixelStreamingClient?.clientParams?.additionalLaunchArgs || []),
    ];

    await handleReconnectGamecast({
      region: gameCastStreamRegion,
      participantId: currentParticipant.participantId,
      websocketSessionId,
      initMessageHandler,
      launchArgs: additionalLaunchArgs,
    });

    if (reconnectCooldownTimeoutRef.current) {
      clearTimeout(reconnectCooldownTimeoutRef.current);
    }
    reconnectCooldownTimeoutRef.current = setTimeout(() => {
      reconnectInProgressRef.current = false;

      if (pendingReconnectRequestRef.current) {
        pendingReconnectRequestRef.current = false;
        setReconnectGamecast(true);
      }
    }, GAMECAST_RECONNECT_COOLDOWN_MS);
  }, [
    initMessageHandler,
    handleReconnectGamecast,
    shouldReconnectGamecast,
    launchArgs,
    websocketSessionId,
    currentRoom,
    maxGamecastReconnectAttempts,
  ]);

  return {
    isStreamLoading,
    isStreamLoaded,
    setIsStreamLoaded,
    setIsStreamLoading,
    videoRef,
    audioRef,
    closeGamecastStream,
    abortGamecastConnection,
    gamecastClientRef,
    processActiveStreamSession,
    sendMytaverseActions,
    initializeGamecastStream,
    handleReconnectGamecast,
    trackAnalytics,
    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
    setReconnectGamecast,
    resetReconnectGamecastAttempts,
  };
};
